import { h, render } from "preact";
import { applyOps } from "@overlay/runtime-patcher";
import type { EditOp } from "@overlay/op-schema";
import { OverlayApp } from "./ui/OverlayApp";
import { styles } from "./ui/styles";

type LoaderConfig = {
  project: string;
  env: string;
  apiBase: string;
  fragmentKey: string;
  storageKey: string;
};

const FRAGMENT_KEY_DEFAULT = "overlay";
const TOKEN_PREFIX = "overlay.token.";

type StoredSession = {
  token: string;
  expiresAt: number;
  projectId: string;
  environment: string;
};

function readConfig(script: HTMLScriptElement | null): LoaderConfig {
  const ds = script?.dataset ?? ({} as DOMStringMap);
  const apiBase = ds.apiBase ?? new URL(script?.src ?? location.href).origin;
  const project = ds.project ?? "demo";
  return {
    project,
    env: ds.env ?? "production",
    apiBase,
    fragmentKey: ds.fragmentKey ?? FRAGMENT_KEY_DEFAULT,
    storageKey: `${TOKEN_PREFIX}${project}`,
  };
}

function parseFragment(fragmentKey: string): string | null {
  const hash = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const val = params.get(fragmentKey);
  return val && val.length > 0 ? val : null;
}

function scrubFragment(fragmentKey: string) {
  const hash = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
  const params = new URLSearchParams(hash);
  params.delete(fragmentKey);
  const rest = params.toString();
  const newUrl = `${location.pathname}${location.search}${rest ? `#${rest}` : ""}`;
  try {
    history.replaceState(null, "", newUrl);
  } catch {
    /* ignore */
  }
}

function loadStoredSession(config: LoaderConfig): StoredSession | null {
  try {
    const raw = localStorage.getItem(config.storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (
      typeof parsed?.token !== "string" ||
      typeof parsed?.expiresAt !== "number" ||
      parsed.expiresAt * 1000 < Date.now()
    ) {
      localStorage.removeItem(config.storageKey);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

async function requestDemoSession(config: LoaderConfig): Promise<StoredSession | null> {
  try {
    const res = await fetch(`${config.apiBase}/api/demo/session`, {
      method: "POST",
      headers: { "content-type": "application/json" },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { token: string; expiresAt: number };
    const session: StoredSession = {
      token: data.token,
      expiresAt: data.expiresAt,
      projectId: config.project,
      environment: config.env,
    };
    localStorage.setItem(config.storageKey, JSON.stringify(session));
    return session;
  } catch {
    return null;
  }
}

async function unlockWithKey(config: LoaderConfig, key: string): Promise<StoredSession | null> {
  try {
    const res = await fetch(`${config.apiBase}/api/auth/unlock`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        key,
        projectId: config.project,
        environment: config.env,
      }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { token: string; expiresAt: number };
    const session: StoredSession = {
      token: data.token,
      expiresAt: data.expiresAt,
      projectId: config.project,
      environment: config.env,
    };
    localStorage.setItem(config.storageKey, JSON.stringify(session));
    return session;
  } catch {
    return null;
  }
}

function clearStoredSession(config: LoaderConfig) {
  try {
    localStorage.removeItem(config.storageKey);
  } catch {
    /* ignore */
  }
}

function mount(config: LoaderConfig, session: StoredSession) {
  if (document.getElementById("__overlay_host__")) return;
  const host = document.createElement("div");
  host.id = "__overlay_host__";
  host.style.position = "fixed";
  host.style.inset = "0";
  host.style.pointerEvents = "none";
  host.style.zIndex = "2147483647";
  document.documentElement.appendChild(host);

  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = styles;
  shadow.appendChild(style);

  const container = document.createElement("div");
  container.className = "overlay-root";
  shadow.appendChild(container);

  render(
    h(OverlayApp, {
      config,
      session,
      signOut: () => {
        clearStoredSession(config);
        location.reload();
      },
    }),
    container,
  );
}

async function fetchSavedPatches(config: LoaderConfig): Promise<EditOp[]> {
  try {
    const url = new URL(`${config.apiBase}/api/patches`);
    url.searchParams.set("project", config.project);
    url.searchParams.set("env", config.env);
    url.searchParams.set("path", location.pathname);
    const res = await fetch(url.toString(), { credentials: "omit" });
    if (!res.ok) return [];
    const data = (await res.json()) as { ops?: EditOp[] };
    return Array.isArray(data.ops) ? data.ops : [];
  } catch {
    return [];
  }
}

function whenReady(cb: () => void) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", cb, { once: true });
  } else {
    cb();
  }
}

async function boot() {
  const script = document.currentScript as HTMLScriptElement | null;
  const config = readConfig(script);

  const keyFromFragment = parseFragment(config.fragmentKey);
  let session = loadStoredSession(config);

  if (keyFromFragment) {
    scrubFragment(config.fragmentKey);
    const unlocked = await unlockWithKey(config, keyFromFragment);
    if (unlocked) session = unlocked;
  }

  if (!session && config.project === "demo") {
    session = await requestDemoSession(config);
  }

  const opsPromise = fetchSavedPatches(config);

  whenReady(async () => {
    const ops = await opsPromise;
    if (ops.length > 0) applyOps(ops);
    if (session) mount(config, session);
  });
}

boot();
